// ScopeContext
//
// "Scope" = the named selection the user is currently analyzing across
// the scoped analysis pages (Overview, Data Trends, Collaboration
// Analytics, Insights, Raw Data, Quick Refer, …). It can be:
//
//   { kind: 'customer', id, label }   — a Salesforce customer record
//   { kind: 'filter',   id, label }   — a saved facility filter
//   { kind: 'facility', id, label }   — a single VitalEngine facility
//
// The scope is sticky across navigation between analysis pages so a user
// can pick "HCA San Antonio Division" once on My Customers and then jump
// between Insights → Raw Data → Quick Refer without re-selecting it.
//
// Persistence: sessionStorage. Survives reloads in the same tab; clears
// when the tab/window closes. Each analysis page subscribes via useScope().
//
// Source-of-truth contract: page-local state (selectedFacilityIds, etc.)
// always reflects the current scope. The page calls setScope() whenever
// the user picks a customer / filter from its in-page dropdown, and
// clearScope() whenever they clear it (or pick "None"). When scope is
// changed *externally* (sticky bar X, or another page mutating it), each
// page's effect detects the mismatch and re-syncs.

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'vitaldata.activeScope';

const ScopeContext = createContext({
  scope: null,
  setScope: () => {},
  clearScope: () => {},
});

function readInitialScope() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.kind || parsed.id == null) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function ScopeProvider({ children }) {
  const [scope, setScopeState] = useState(readInitialScope);

  // Mirror to sessionStorage every time scope changes so a page reload in
  // the same tab keeps the user's context.
  useEffect(() => {
    try {
      if (scope) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch { /* private mode / quota — ignore */ }
  }, [scope]);

  const setScope = useCallback((next) => {
    if (!next || !next.kind || next.id == null) {
      setScopeState(null);
      return;
    }
    setScopeState({
      kind: next.kind,
      id: String(next.id),
      label: next.label || '',
    });
  }, []);

  const clearScope = useCallback(() => setScopeState(null), []);

  return (
    <ScopeContext.Provider value={{ scope, setScope, clearScope }}>
      {children}
    </ScopeContext.Provider>
  );
}

export function useScope() {
  return useContext(ScopeContext);
}

export default ScopeContext;
